import React, { useCallback, useContext } from 'react';
import { FlexColumn, FlexMiddle, FormModal, InputField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ContractTypesWithMinimums, CONTRACT_TYPES_WITH_TOIL_ACCRUAL } from '@ubeya/shared/constants';
import { getDirtyFields, isPositiveNumber, isValueBetweenNumberRange } from '@ubeya/shared/utils/form';
import styled from 'styled-components';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';

const Container = styled(FlexColumn)`
  gap: 16px;
`;

const FieldsArea = styled(FlexMiddle)`
  padding: 10px 20px;
  justify-content: space-between;
  gap: 16px;
  > div {
    width: 50%;
  }
`;

const AdjustEmployeeContractModal = ({ onClose, adjustEmployeeContract, contract }) => {
  const { currency, t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const minimumWeeklyWorkingHours = contract.employeeMinimumWeeklyWorkingHours || contract.minimumWeeklyWorkingHours;
  const minimumHourlyRateOfPay = contract.employeeMinimumHourlyRateOfPay || contract.minimumHourlyRateOfPay;
  const maxDaysConsumeInWeek = contract.employeeMaxDaysConsumeInWeek || contract.maxDaysConsumeInWeek;

  const holidayAccrualYearlyEntitlementAdjustment = contract.holidayAccrualYearlyEntitlementAdjustment || 0;
  const sickDaysYearlyEntitlementAdjustment = contract.sickDaysYearlyEntitlementAdjustment || 0;
  const holidayAccrualYearlyEntitlement =
    contract.employeeHolidayAccrualYearlyEntitlement || contract.holidayAccrualYearlyEntitlement || 0;
  const sickDaysYearlyEntitlement =
    contract.employeeSickDaysYearlyEntitlement || contract.sickDaysYearlyEntitlement || 0;

  const shouldViewMinimumOptions = ContractTypesWithMinimums.includes(contract.type);
  const shouldViewToilFields = CONTRACT_TYPES_WITH_TOIL_ACCRUAL.includes(contract.type);

  const employmentRate = contract.employmentRate;
  const hoursPerWorkdayOverride = contract.employeeHoursPerWorkday ?? contract.hoursPerWorkday;

  const initialValues = {
    holidayAccrualYearlyEntitlementAdjustment:
      holidayAccrualYearlyEntitlementAdjustment + holidayAccrualYearlyEntitlement,
    sickDaysYearlyEntitlementAdjustment: sickDaysYearlyEntitlementAdjustment + sickDaysYearlyEntitlement,
    maxDaysConsumeInWeek,
    minimumWeeklyWorkingHours,
    minimumHourlyRateOfPay,
    employmentRate,
    hoursPerWorkday: hoursPerWorkdayOverride
  };

  const onSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, dirtyFields);
      if (!dirtyValues) return;

      if (dirtyValues.holidayAccrualYearlyEntitlementAdjustment) {
        const value = dirtyValues.holidayAccrualYearlyEntitlementAdjustment - holidayAccrualYearlyEntitlement;
        dirtyValues.holidayAccrualYearlyEntitlementAdjustment = +formattingMap.fixedNumber(value, 2);
      }
      if (dirtyValues.sickDaysYearlyEntitlementAdjustment) {
        const value = dirtyValues.sickDaysYearlyEntitlementAdjustment - sickDaysYearlyEntitlement;
        dirtyValues.sickDaysYearlyEntitlementAdjustment = +formattingMap.fixedNumber(value, 2);
      }

      await adjustEmployeeContract({
        ...dirtyValues,
        employeeContractId: contract.employeeContractId
      });
      onClose();
    },
    [
      adjustEmployeeContract,
      contract.employeeContractId,
      holidayAccrualYearlyEntitlement,
      sickDaysYearlyEntitlement,
      formattingMap,
      onClose
    ]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={onSubmit}
      size="medium"
      onClose={onClose}
      title={t('contractTypes_employeeSpecificContractTerms')}
      confirmText={t('update')}
      showCancelButton>
      <Container>
        {shouldViewMinimumOptions && (
          <FieldsArea>
            <InputField
              title={t('contractTypes_minimumWeeklyWorkingHours')}
              name="minimumWeeklyWorkingHours"
              type="number"
              validate={isPositiveNumber}
            />
            <InputField
              title={`${t('contractTypes_minimumHourlyRateOfPay')} (${currency})`}
              name="minimumHourlyRateOfPay"
              type="number"
              validate={isPositiveNumber}
            />
          </FieldsArea>
        )}

        <FieldsArea>
          <InputField
            name="holidayAccrualYearlyEntitlementAdjustment"
            title={t('contractTypes_holidayAccrualYearlyEntitlementAdjustment')}
            type="number"
            validate={isPositiveNumber}
          />
          <InputField
            name="maxDaysConsumeInWeek"
            title={t('contractTypes_maxDaysToConsumeInAWeek')}
            type="number"
            validate={(value) => isValueBetweenNumberRange({ value, min: 1, max: 7, isRequired: false })}
          />
        </FieldsArea>

        <FieldsArea>
          <InputField
            name="sickDaysYearlyEntitlementAdjustment"
            title={t('contractTypes_sickDaysYearlyEntitlementAdjustment')}
            type="number"
            validate={isPositiveNumber}
          />
        </FieldsArea>

        {shouldViewToilFields && (
          <FieldsArea>
            <InputField
              name="employmentRate"
              title={t('contractTypes_employmentRate')}
              type="number"
              validate={(value) => isValueBetweenNumberRange({ value, min: 0, max: 100, isRequired: false })}
            />
            <InputField
              name="hoursPerWorkday"
              title={t('contractTypes_hoursPerWorkday')}
              type="number"
              validate={(value) => isValueBetweenNumberRange({ value, min: 1, max: 24, isRequired: false })}
            />
          </FieldsArea>
        )}
      </Container>
    </FormModal>
  );
};

export default AdjustEmployeeContractModal;
